import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { configClient } from '../lib/supabase/configClient';
import type { SupportedLanguages } from '../i18n';
import { useHotelConfig } from '../hooks/useHotelConfig';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  type?: 'website' | 'article';
}

export const SEO: React.FC<SEOProps> = ({
  title: propTitle,
  description: propDescription,
  image: propImage,
  type = 'website'
}) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language as SupportedLanguages;
  const { slug, baseUrl } = useHotelConfig();

  // Load configuration
  const { data: config } = useQuery({
    queryKey: ['config'],
    queryFn: () => configClient.getConfig(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  if (!config) return null;

  const baseAppUrl = 'https://www.conciergify-app.com';
  const hotelBaseUrl = `${baseAppUrl}/${slug}`;
  const currentUrl = `${hotelBaseUrl}${location.pathname.replace(baseUrl, '')}`;
  
  const title = propTitle || config.name;
  const description = propDescription || 'Votre concierge personnel pour tout simplifier ! Restaurants, spectacles et services utiles à deux pas de votre hôtel. Découvrez et réservez en quelques clics.';
  const image = propImage || `${baseAppUrl}/images/social/og-image.jpg`;

  // Generate JSON-LD schema
  const generateSchema = () => {
    const baseSchema = {
      "@context": "https://schema.org",
      "@type": "Hotel",
      "name": config.name,
      "description": description,
      "url": hotelBaseUrl,
      "telephone": config.telephone,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": config.fulladress,
        "addressLocality": "Paris",
        "postalCode": "75009",
        "addressCountry": "FR"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": config.latitude,
        "longitude": config.longitude
      },
      "image": image
    };

    // Add specific data based on page type
    if (location.pathname.includes('/event/')) {
      return {
        ...baseSchema,
        "@type": "Event",
        "location": {
          "@type": "Place",
          "name": config.name,
          "address": baseSchema.address
        }
      };
    }

    return baseSchema;
  };

  return (
    <>
      {/* Basic meta tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="language" content={currentLanguage} />
      <link rel="canonical" href={currentUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content={config.name} />
      <meta property="og:locale" content={currentLanguage} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${baseAppUrl}/images/social/twitter-card.jpg`} />
      <meta name="twitter:image:width" content="1200" />
      <meta name="twitter:image:height" content="600" />

      {/* Schema.org JSON-LD */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(generateSchema()) }}
      />

      {/* Hreflang tags for internationalization */}
      <link rel="alternate" hrefLang="x-default" href={hotelBaseUrl} />
      {config.available_languages?.map((lang: string) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={`${hotelBaseUrl}/${lang}${location.pathname.replace(baseUrl, '')}`} />
      ))}
    </>
  );
}